/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useMemo, useContext, useRef, useEffect } from 'react';
import { SelectProps } from './types';
import { KEYS } from '../utils/constants';
import { ID } from '../utils/arrays';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';
import { ArrowContainer } from './styles';
import BasicIcon from '../BasicIcon/BasicIcon';
import DropDown from '../DropDown/DropDown';

const Select: React.FC<SelectProps> = (props) => {
  const {
    meta,
    highlightedIndex,
    options,
    size,
    variant = 'underline',
    readOnly,
    disabled,
    isOpen,
    input,
    dropDownOnly,
    inputRef,
    isLoading,
    overlayPosition = 'auto',
    overAll = false,
    pointer = true,
    testId,
    expand,
    setHighlightedIndex,
    onSelection,
    showChevron = true,
  } = props;
  const chevronRef = useRef<HTMLDivElement>(null);
  const scrollRef: React.Ref<HTMLInputElement> = useRef(null);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const { actualTheme, isMobile } = useContext(ThemeContext);
  const [generatedId] = useState(ID());
  const [borderIndexes, setBorderIndexes] = useState({ first: 0, last: 3 });

  const onKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      switch (event.code) {
        case KEYS.ArrowUp:
          event.preventDefault();
          if (highlightedIndex.index > 0) {
            const newIndex = highlightedIndex.index - 1;
            setHighlightedIndex({
              index: newIndex,
              id: highlightedIndex.id,
            });
            if (newIndex === borderIndexes.first && newIndex > 0) {
              scrollRef.current?.scroll({
                top: scrollRef.current.scrollTop - 32,
                behavior: 'auto',
              });
              setBorderIndexes({ first: borderIndexes.first - 1, last: borderIndexes.last - 1 });
            }
          }
          break;
        case KEYS.ArrowDown:
          event.preventDefault();
          if (highlightedIndex.index < options.length - 1) {
            const newIndex = highlightedIndex.index + 1;
            setHighlightedIndex({
              index: newIndex,
              id: highlightedIndex.id,
            });
            if (newIndex === borderIndexes.last && newIndex < options.length - 1) {
              scrollRef.current?.scroll({
                top: scrollRef.current.scrollTop + 32,
                behavior: 'auto',
              });
              setBorderIndexes({ first: borderIndexes.first + 1, last: borderIndexes.last + 1 });
            }
          }
          break;
        case KEYS.Enter:
          event.preventDefault();
          onSelection(options[highlightedIndex.index]);
          setTimeout(() => inputRef.current.blur()); // Horrible
          break;

        default:
          break;
      }
    },
    [highlightedIndex, options, onSelection, setHighlightedIndex, borderIndexes, inputRef],
  );

  const inputSize = useMemo(() => {
    if (!size && isMobile) return 'small';
    return size || 'standard';
  }, [size, isMobile]);

  const inputHeight = useMemo(() => {
    const s = inputSize || 'standard';
    const h = actualTheme.input.sizes[s].height[variant];
    return h;
  }, [actualTheme, inputSize, variant]);

  useEffect(() => {
    if (isOpen) {
      scrollRef.current?.scroll({
        top: 0,
        behavior: 'auto',
      });
      setHighlightedIndex({ index: 0, id: '' });
      setBorderIndexes({ first: 0, last: 3 });
    }
  }, [isOpen, setHighlightedIndex]);

  const renderChevronIcon = useCallback(
    () => (
      <>
        {showChevron ? (
          <ArrowContainer
            about="chevron"
            onClick={!isLoading ? expand : undefined}
            isOpen={isOpen}
            disabled={disabled}
            readOnly={readOnly}
            isLoading={isLoading}
            hasError={hasError}
            data-testid={`${testId}-chevron`}
            ref={chevronRef}
          >
            <BasicIcon size="medium" name={isLoading ? 'spinner' : 'chevron-down'} clickable />
          </ArrowContainer>
        ) : (
          isLoading && <BasicIcon size="medium" name="spinner" />
        )}
      </>
    ),
    [showChevron, isLoading, expand, isOpen, disabled, readOnly, hasError, testId],
  );
  return (
    <>
      <Input
        {...props}
        id={generatedId}
        readOnly={readOnly || dropDownOnly}
        onKeyDown={onKeyPress}
        rightAddOn={renderChevronIcon()}
        pointer={pointer}
      />
      <DropDown
        {...props}
        overAll={overAll}
        target={generatedId}
        inputValue={input?.value}
        targetHeight={inputHeight}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        scrollRef={scrollRef}
        overlayPosition={overlayPosition}
        chevronRef={chevronRef}
      />
    </>
  );
};

export default Select;
